import * as R from 'ramda';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthActions, AuthReducer, PlanTenant, TenantInfo, UserRole } from '@/features/auth';
import { storage } from '@/utils';

export const useAuth = () => {
    const dispatch = useDispatch();

    const user = useSelector(R.pipe(AuthReducer.getReducerState, AuthReducer.selectors.getUser));
    const currentTenant = useSelector(R.pipe(AuthReducer.getReducerState, AuthReducer.selectors.getTenant));
    const invitationToken = useSelector(R.pipe(AuthReducer.getReducerState, AuthReducer.selectors.getInvitationToken));

    const isAuthenticated = useMemo(() => !!(user && user._id), [user]);

    const isEmailVerified = useMemo(() => !!(isAuthenticated && user!.emailVerified), [isAuthenticated, user]);

    const isEmptyTenant = useMemo(
        () => !!(isAuthenticated && isEmailVerified && !user!.tenants.some((tenant: TenantInfo) => tenant.status === 'active')),
        [isAuthenticated, isEmailVerified, user]
    );

    const currentUserRolesIds = useMemo(() => {
        if (!user || !currentTenant) {
            return [];
        }
        const tenant = user.tenants
            .filter((tenantUser) => tenantUser.status === 'active')
            .find((tenantUser) => tenantUser.tenant.id === currentTenant.id);
        if (!tenant) {
            return [];
        }
        return tenant.roles;
    }, [currentTenant, user]);

    const currentTenantPlan = useMemo(() => {
        if (!currentTenant) {
            return PlanTenant.Free;
        }
        if (!currentTenant.plan) {
            return PlanTenant.Free;
        }
        return currentTenant.plan;
    }, [currentTenant]);

    const planMatchOneOf = useCallback(
        (arg) => {
            if (!currentTenantPlan) {
                return false;
            }
            if (!arg) {
                return false;
            }
            if (Array.isArray(arg)) {
                if (!arg.length) {
                    return false;
                }
                return arg.some((plan) => plan === currentTenantPlan);
            }
            return arg === currentTenantPlan;
        },
        [currentTenantPlan]
    );

    const rolesMatchOneOf = useCallback(
        (arg) => {
            if (!currentUserRolesIds) {
                return false;
            }
            if (!arg) {
                return false;
            }
            if (Array.isArray(arg)) {
                if (!arg.length) {
                    return false;
                }
                return arg.some((role) => currentUserRolesIds.includes(role));
            }
            return currentUserRolesIds.includes(arg);
        },
        [currentUserRolesIds]
    );

    const matchPermission = useCallback(
        (permission) => {
            if (!permission) {
                return true;
            }
            if (!planMatchOneOf(permission.allowedPlans)) {
                return false;
            }
            return rolesMatchOneOf(permission.allowedRoles);
        },
        [planMatchOneOf, rolesMatchOneOf]
    );

    const lockedForCurrentPlan = useCallback(
        (permission) => {
            if (!permission) {
                return false;
            }

            if (!rolesMatchOneOf(permission.allowedRoles)) {
                return false;
            }

            return !planMatchOneOf(permission.allowedPlans);
        },
        [planMatchOneOf, rolesMatchOneOf]
    );

    useEffect(() => {
        const token = storage.getToken();
        if (!token || !isAuthenticated) {
            dispatch(AuthActions.logout());
        }
    }, [dispatch, isAuthenticated]);

    return {
        user,
        invitationToken,
        tenant: currentTenant,
        isAuthenticated,
        isEmailVerified,
        isEmptyTenant,
        matchPermission,
        lockedForCurrentPlan
    };
};
